import React, { useState, useEffect } from 'react';
import { orderAPI, userAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';

function CODSettlement() {
  const { user } = useAuth();
  const [settledOrders, setSettledOrders] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [showBankForm, setShowBankForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bankFormData, setBankFormData] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    branch: '',
    accountType: 'savings',
    upiId: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    fetchSettledOrders();
    fetchBankDetails();
  }, []);

  const fetchSettledOrders = async () => {
    try {
      const response = await orderAPI.getDeliveryPartnerOrders();
      const orders = response.data.data.orders;

      // Filter for settled COD orders
      const settled = orders.filter(order =>
        order.paymentMethod === 'cash' &&
        order.paymentStatus === 'received' &&
        order.codSettled === true
      );

      setSettledOrders(settled);
    } catch (error) {
      console.error('Failed to fetch settled orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const response = await userAPI.getProfile();
      setBankDetails(response.data.data.user.bankDetails);
    } catch (error) {
      console.error('Failed to fetch bank details:', error);
    }
  };

  const handleBankFormChange = (e) => {
    const { name, value } = e.target;
    setBankFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBankFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await userAPI.updateProfile({ bankDetails: bankFormData });
      alert('✅ Bank details saved successfully!');
      setBankDetails(bankFormData);
      setShowBankForm(false);
    } catch (error) {
      alert('Failed to save bank details: ' + (error.response?.data?.message || error.message));
    }
  };

  const totalSettled = settledOrders.reduce((sum, order) => sum + order.pricing.total, 0);

  if (loading) return <div className="loading">🔄 Loading COD settlements...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            💰 COD Settlement
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Request payouts for your settled cash deliveries
          </p>
        </div>

        {/* Summary Card */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-lg)'
          }}>
            <div className="stat-card" style={{ borderLeftColor: 'var(--success)' }}>
              <div className="stat-icon">💵</div>
              <div className="stat-value">₹{totalSettled}</div>
              <div className="stat-label">Total COD Collected</div>
            </div>

            <div className="stat-card" style={{ borderLeftColor: 'var(--primary)' }}>
              <div className="stat-icon">📦</div>
              <div className="stat-value">{settledOrders.length}</div>
              <div className="stat-label">Settled Orders</div>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
          <h2 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)'
          }}>
            🏦 Bank Account Details
          </h2>

          {!bankDetails || !bankDetails.accountNumber ? (
            <div style={{
              padding: 'var(--space-xl)',
              background: 'rgba(255, 184, 77, 0.1)',
              borderRadius: 'var(--radius-md)',
              border: '2px solid rgba(255, 184, 77, 0.3)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>⚠️</div>
              <p style={{ color: 'var(--warning)', marginBottom: 'var(--space-md)', fontWeight: '600' }}>
                No bank account added yet
              </p>
              <button
                onClick={() => setShowBankForm(true)}
                className="btn btn-primary"
              >
                ➕ Add Bank Account
              </button>
            </div>
          ) : (
            <div style={{
              padding: 'var(--space-lg)',
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: 'var(--radius-md)',
              border: '2px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-md)' }}>
                <div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                    Account Holder
                  </p>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: '700', color: 'var(--gray-900)', margin: 0 }}>
                    {bankDetails.accountHolderName}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                    Account Number
                  </p>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: '700', color: 'var(--gray-900)', margin: 0 }}>
                    ****{bankDetails.accountNumber.slice(-4)}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                    Bank & Branch
                  </p>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: '700', color: 'var(--gray-900)', margin: 0 }}>
                    {bankDetails.bankName}, {bankDetails.branch}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                    IFSC Code
                  </p>
                  <p style={{ fontSize: 'var(--font-size-base)', fontWeight: '700', color: 'var(--gray-900)', margin: 0 }}>
                    {bankDetails.ifscCode}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBankForm(true)}
                className="btn btn-sm btn-secondary"
                style={{ marginTop: 'var(--space-md)' }}
              >
                ✏️ Edit Bank Details
              </button>
            </div>
          )}
        </div>

        {/* Bank Form Modal */}
        {showBankForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-lg)'
          }}>
            <div className="card" style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'var(--gray-900)'
              }}>
                🏦 Bank Account Details
              </h2>

              <form onSubmit={handleBankFormSubmit}>
                <div className="form-group">
                  <label>Account Holder Name *</label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={bankFormData.accountHolderName}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Account Number *</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={bankFormData.accountNumber}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>IFSC Code *</label>
                  <input
                    type="text"
                    name="ifscCode"
                    value={bankFormData.ifscCode}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Bank Name *</label>
                  <input
                    type="text"
                    name="bankName"
                    value={bankFormData.bankName}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Branch *</label>
                  <input
                    type="text"
                    name="branch"
                    value={bankFormData.branch}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Account Type *</label>
                  <select
                    name="accountType"
                    value={bankFormData.accountType}
                    onChange={handleBankFormChange}
                    required
                  >
                    <option value="savings">Savings</option>
                    <option value="current">Current</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>UPI ID (Optional)</label>
                  <input
                    type="text"
                    name="upiId"
                    value={bankFormData.upiId}
                    onChange={handleBankFormChange}
                    placeholder="yourname@upi"
                  />
                </div>

                <div className="form-group">
                  <label>Address *</label>
                  <input
                    type="text"
                    name="address"
                    value={bankFormData.address}
                    onChange={handleBankFormChange}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={bankFormData.city}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="state"
                    value={bankFormData.state}
                    onChange={handleBankFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={bankFormData.pincode}
                    onChange={handleBankFormChange}
                    pattern="[0-9]{6}"
                    placeholder="6-digit pincode"
                    required
                  />
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-xl)' }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                    💾 Save Bank Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBankForm(false)}
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Settled Orders */}
        <div className="card fade-in">
          <h2 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-lg)'
          }}>
            📦 Settled COD Orders
          </h2>

          {settledOrders.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--space-2xl)',
              background: 'var(--gray-100)',
              borderRadius: 'var(--radius-md)'
            }}>
              <div style={{ fontSize: '80px', marginBottom: 'var(--space-md)' }}>📭</div>
              <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
                No settled COD orders available for payout
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
              {settledOrders.map(order => (
                <div
                  key={order._id}
                  style={{
                    padding: 'var(--space-md)',
                    background: 'var(--gray-100)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid var(--gray-200)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <p style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '700',
                      color: 'var(--gray-900)',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      Order #{order.orderNumber}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-600)',
                      margin: 0
                    }}>
                      Settled on {new Date(order.codSettlementDate || order.createdAt).toLocaleDateString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: '800',
                    color: 'var(--success)'
                  }}>
                    ₹{order.pricing.total}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CODSettlement;
